import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Radio, Heart, MessageCircle, Share2, Bookmark, TrendingUp, TrendingDown, Zap, BarChart3, Users } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { GrowthChart } from "@/components/GrowthChart";
import { ChartSkeleton, MetricSkeleton } from "@/components/Skeletons";
import { MetricCard } from "@/components/MetricCard";
import api from "@/lib/api";
import { formatNumber, formatCompact, formatPct } from "@/lib/format";
import { useT } from "@/i18n";

const RANGES = ["7", "30", "90", "365", "all"];

export function Analytics() {
  const { t } = useT();
  const [range, setRange] = useState("30");
  const { data, isLoading } = useQuery({
    queryKey: ["analytics", range],
    queryFn: async () => (await api.get(`/analytics?range=${range}`)).data,
    retry: false,
  });

  const rows = data ? [
    { icon: Eye, label: t("common.views"), value: formatNumber(data.metrics.views) },
    { icon: Radio, label: t("common.reach"), value: formatNumber(data.metrics.reach) },
    { icon: BarChart3, label: t("common.impressions"), value: formatNumber(data.metrics.impressions) },
    { icon: Heart, label: t("common.likes"), value: formatNumber(data.metrics.likes) },
    { icon: MessageCircle, label: t("common.comments"), value: formatNumber(data.metrics.comments) },
    { icon: Share2, label: t("common.shares"), value: formatNumber(data.metrics.shares) },
    { icon: Bookmark, label: t("common.saves"), value: formatNumber(data.metrics.saves) },
    { icon: Zap, label: t("analytics.avgPerReel"), value: formatCompact(data.metrics.avg_views) },
  ] : [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("analytics.title")}</h1>
            <p className="mt-1 text-muted-foreground">{t("analytics.subtitle")}</p>
          </div>
          <div className="flex flex-wrap gap-1 rounded-xl bg-muted p-1">
            {RANGES.map((r) => (
              <button key={r} onClick={() => setRange(r)} data-testid={`analytics-range-${r}`}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${range === r ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                {t(`dashboard.ranges.${r}`)}
              </button>
            ))}
          </div>
        </div>

        {data?.clamped && (
          <div className="rounded-xl border border-brand-500/30 bg-brand-500/10 px-4 py-2.5 text-sm text-brand-700 dark:text-brand-300" data-testid="clamp-notice">
            {t("analytics.clampedNotice")}
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">{[0,1,2,3].map(i => <MetricSkeleton key={i} />)}</div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard index={0} icon={TrendingUp} label={t("analytics.followersGained")} value={formatNumber(data.followers_gained)} accent="emerald" />
            <MetricCard index={1} icon={TrendingDown} label={t("analytics.followersLost")} value={formatNumber(data.followers_lost)} accent="rose" />
            <MetricCard index={2} icon={Users} label={t("analytics.followersNet")} value={`${data.followers_net >= 0 ? "+" : ""}${formatNumber(data.followers_net)}`} accent="brand" />
            <MetricCard index={3} icon={Zap} label={t("analytics.growthPct")} value={formatPct(data.growth_pct)} accent="amber" />
          </div>
        )}

        {isLoading ? <ChartSkeleton /> : (
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <h3 className="mb-5 font-display text-lg font-semibold">{t("dashboard.followerGrowth")}</h3>
            <GrowthChart data={data.follower_history} />
          </div>
        )}

        {!isLoading && (
          <div className="rounded-2xl border border-border bg-card p-2 shadow-sm">
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-border sm:grid-cols-4">
              {rows.map((r) => (
                <div key={r.label} className="flex flex-col gap-2 bg-card p-5" data-testid={`analytics-metric-${r.label}`}>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <r.icon className="h-4 w-4" /><span className="text-xs font-mono uppercase tracking-wide">{r.label}</span>
                  </div>
                  <div className="font-display text-xl font-bold">{r.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
